/**
 * Organization detail (ADMIN-PR1-b-orgs) — GET /v1/admin/organizations/{id}
 * (incl. members), with per-member org-role assign/revoke:
 *   PUT/DELETE /v1/admin/organizations/{org_id}/members/{user_id}/roles/{role}
 *
 * DERIVED view: name/created_at are non-authoritative. All dual-gated via adminApi.
 */
import { useState, type ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import {
  assignOrgRole,
  getOrganization,
  revokeOrgRole,
  type OrganizationDetail,
  type OrgRole,
} from "../auth/adminApi";
import { errorMessage, useAsync } from "../lib/useAsync";
import { ErrorBanner, PageHeader, Spinner } from "../components/ui";

const ORG_ROLES: OrgRole[] = ["owner", "admin", "member"];

function fmtDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

export function OrgDetailPage(): ReactNode {
  const { id = "" } = useParams();
  const { data, loading, error, reload } = useAsync<OrganizationDetail>(
    () => getOrganization(id),
    [id],
  );
  const [busy, setBusy] = useState<string | null>(null);
  const [mutationError, setMutationError] = useState<string | null>(null);

  async function onToggleRole(userId: string, role: OrgRole, has: boolean) {
    const verb = has ? "Revoke" : "Assign";
    if (!window.confirm(`${verb} the "${role}" role for this member?`)) return;
    setMutationError(null);
    setBusy(`${userId}:${role}`);
    try {
      if (has) await revokeOrgRole(id, userId, role);
      else await assignOrgRole(id, userId, role);
      reload();
    } catch (err) {
      setMutationError(errorMessage(err));
    } finally {
      setBusy(null);
    }
  }

  if (loading) return <Spinner />;
  if (error) return <ErrorBanner message={errorMessage(error)} />;
  if (!data) return <ErrorBanner message="Organization not found." />;

  return (
    <div className="space-y-5">
      <div>
        <Link to="/orgs" className="text-sm text-oxbo hover:underline">
          ← Organizations
        </Link>
      </div>
      <PageHeader title={data.name ?? "Unnamed organization"} />

      <p className="text-xs text-slate-500">
        Derived view — name/created are best-effort and not authoritative.
      </p>

      {mutationError && <ErrorBanner message={mutationError} />}

      <section className="rounded-lg border border-slate-200 bg-white p-5">
        <dl className="grid grid-cols-1 gap-x-8 gap-y-2 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-xs uppercase tracking-wide text-slate-400">Org ID</dt>
            <dd className="font-mono text-xs text-slate-700">{data.id}</dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wide text-slate-400">Created (derived)</dt>
            <dd className="text-slate-700">{fmtDate(data.created_at)}</dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wide text-slate-400">Members</dt>
            <dd className="text-slate-700">{data.member_count}</dd>
          </div>
        </dl>
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-5">
        <h2 className="mb-3 text-sm font-semibold text-slate-700">Members &amp; roles</h2>
        {data.members.length === 0 ? (
          <p className="text-sm text-slate-500">No members.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-slate-200 text-slate-500">
                <tr>
                  <th className="px-2 py-2 font-medium">Member</th>
                  <th className="px-2 py-2 font-medium">Roles</th>
                </tr>
              </thead>
              <tbody>
                {data.members.map((m) => (
                  <tr key={m.user_id} className="border-b border-slate-100 last:border-0 align-top">
                    <td className="px-2 py-2">
                      <Link to={`/users/${m.user_id}`} className="text-oxbo hover:underline">
                        {m.email}
                      </Link>
                      <div className="font-mono text-[10px] text-slate-400">{m.user_id}</div>
                    </td>
                    <td className="px-2 py-2">
                      <div className="flex flex-wrap gap-2">
                        {ORG_ROLES.map((role) => {
                          const has = m.org_roles.includes(role);
                          const key = `${m.user_id}:${role}`;
                          return (
                            <button
                              key={role}
                              type="button"
                              disabled={busy === key}
                              onClick={() => void onToggleRole(m.user_id, role, has)}
                              title={has ? `Revoke ${role}` : `Assign ${role}`}
                              className={`rounded border px-2 py-1 text-xs disabled:opacity-50 ${
                                has
                                  ? "border-oxbo bg-oxbo text-white hover:bg-oxbo-dark"
                                  : "border-slate-300 text-slate-600 hover:bg-slate-50"
                              }`}
                            >
                              {role}
                            </button>
                          );
                        })}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="mt-2 text-xs text-slate-400">
              Click a role to toggle it for that member (assign / revoke).
            </p>
          </div>
        )}
      </section>
    </div>
  );
}
